'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  TextInput,
  Paper,
  Stack,
  Group,
  Box,
  Image,
  Badge,
  Card,
  SimpleGrid,
  Loader,
  Alert,
  ActionIcon,
  Skeleton,
  Transition,
  Button,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconSearch,
  IconX,
  IconAlertCircle,
  IconZoomQuestion,
  IconMoneybag,
  IconPlus,
  IconMinus,
  IconCheck,
  IconCopy,
  IconTrash,
  IconExternalLink,
} from '@tabler/icons-react';
import Link from 'next/link';
import api from '@/lib/api';
import { notifyError, notifySuccess } from '@/lib/notify';
import { friendlyMessage } from '@/lib/apiError';
import { getThumbUrl } from '@/lib/imageUrl';
import { COMMON_FILTER_COLORS } from '@/lib/legoColors';
import { VerificationBadge } from '@/components/VerificationBadge';
import { useUser } from '@/context/UserContext';
import { PageHero } from '@/components/PageHero';
import { trackEvent } from '@/lib/telemetry';
import {
  FIND_MY_BAG_QUERY_KEY,
  FIND_MY_BAG_SESSION_KEY,
  MAX_FIND_MY_BAG_PARTS,
  findBagPartMatches,
  mergeFindMyBagMetadata,
  normalizeElementId,
  normalizeFindMyBagSelection,
  parseFindMyBagSelection,
  serializeFindMyBagSelection,
  summarizeBagMatch,
} from '@/features/find-my-bag/model';

const PART_OPTIONS_ID = 'findmybag-part-options';
const PART_SEARCH_STATUS_ID = 'findmybag-part-search-status';
const SELECTION_STATUS_ID = 'findmybag-selection-status';

async function copyToClipboard(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('The browser could not copy the link.');
}

function countBucket(count) {
  if (!count) return '0';
  if (count === 1) return '1';
  if (count <= 3) return '2-3';
  if (count <= 10) return '4-10';
  if (count <= 50) return '11-50';
  return '51+';
}

export default function FindBagPage() {
  const { collectionIds } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
  // Kleurfilter: één kleur tegelijk (de backend kent één colorFilter per
  // query); de kleurnaam gaat gewoon de zoekquery in, die herkent hem
  const [selectedColor, setSelectedColor] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectionReady, setSelectionReady] = useState(false);
  const [selectionFeedback, setSelectionFeedback] = useState('');
  const [shareFeedback, setShareFeedback] = useState('');
  const [resolvingManualId, setResolvingManualId] = useState(false);
  const [matchingResults, setMatchingResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchRetry, setSearchRetry] = useState(0);
  const [resultsRetry, setResultsRetry] = useState(0);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const copyFeedbackTimerRef = useRef(null);
  const selectionRevisionRef = useRef(0);
  const trackedResultsRevisionRef = useRef(-1);

  // The compact URL is the shareable source of truth. When it is absent on a
  // fresh visit, restore the richer selection metadata from this tab's session.
  // A browser back/forward navigation follows the URL instead of resurrecting
  // a newer session selection that belongs to another history entry.
  useEffect(() => {
    const readSavedSelection = () => {
      try {
        return normalizeFindMyBagSelection(
          JSON.parse(sessionStorage.getItem(FIND_MY_BAG_SESSION_KEY) || '[]')
        );
      } catch {
        return [];
      }
    };

    const restoreSelection = async ({ allowSessionFallback }) => {
      const params = new URLSearchParams(window.location.search);
      const hasUrlSelection = params.has(FIND_MY_BAG_QUERY_KEY);
      const fromUrl = parseFindMyBagSelection(params.get(FIND_MY_BAG_QUERY_KEY));
      const saved = readSavedSelection();
      const candidate = hasUrlSelection
        ? mergeFindMyBagMetadata(fromUrl, saved)
        : allowSessionFallback
          ? saved
          : [];
      let restored = candidate;
      if (candidate.length > 0) {
        try {
          const resolved = await api.resolvePartsGlobal(candidate.map(part => part.partId));
          const resolvedById = new Map(resolved.map(part => [String(part.partId), part]));
          restored = candidate
            .filter(part => resolvedById.has(part.partId))
            .map(part => ({ ...part, ...resolvedById.get(part.partId), quantity: part.quantity }));
        } catch (err) {
          // Offline restore still works with saved metadata. The matching call
          // below owns the normal retry/error UI for server availability.
          if (err?.name === 'AbortError') return;
        }
      }
      selectionRevisionRef.current += 1;
      setSelectedParts(restored);
      setSelectionReady(true);
      setSelectionFeedback(
        restored.length > 0
          ? `Restored ${restored.length} selected ${restored.length === 1 ? 'part' : 'parts'}.`
          : ''
      );
    };

    void restoreSelection({ allowSessionFallback: true });
    const handlePopState = () => {
      void restoreSelection({ allowSessionFallback: false });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Keep the full selection in session storage and its compact ID/quantity
  // representation in the address bar. replaceState avoids filling browser
  // history for every quantity tap while still making the current state usable
  // as a copied/shared link.
  useEffect(() => {
    if (!selectionReady) return;
    const normalized = normalizeFindMyBagSelection(selectedParts);
    const serialized = serializeFindMyBagSelection(normalized);
    try {
      if (normalized.length > 0) {
        sessionStorage.setItem(FIND_MY_BAG_SESSION_KEY, JSON.stringify(normalized));
      } else {
        sessionStorage.removeItem(FIND_MY_BAG_SESSION_KEY);
      }
    } catch {
      // Session storage can be unavailable in private/restricted contexts; the
      // URL remains a fully functional persistence and sharing fallback.
    }

    const url = new URL(window.location.href);
    if (serialized) url.searchParams.set(FIND_MY_BAG_QUERY_KEY, serialized);
    else url.searchParams.delete(FIND_MY_BAG_QUERY_KEY);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [selectedParts, selectionReady]);

  // Search for parts globally - de geselecteerde kleur wordt vóór de
  // getypte tekst geplakt ("red 1x2"); alleen een kleur kiezen werkt ook
  const effectiveQuery = selectedColor
    ? `${selectedColor} ${debouncedSearch || ''}`.trim()
    : debouncedSearch || '';
  useEffect(() => {
    if (!effectiveQuery || effectiveQuery.length < 2) {
      return;
    }
    const controller = new AbortController();
    const doSearch = async () => {
      setSearchLoading(true);
      try {
        const results = await api.searchPartsGlobal(effectiveQuery, controller.signal);
        if (controller.signal.aborted) return;
        setSearchResults(results);
        setDropdownOpen(results.length > 0 || !!selectedColor);
        setActiveOptionIndex(-1);
      } catch (err) {
        if (err.name === 'AbortError' || controller.signal.aborted) return;
        console.error(err);
        notifyError(err, {
          title: 'Part search failed',
          id: 'findmybag-search',
          onRetry: () => setSearchRetry(k => k + 1),
        });
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    };
    doSearch();
    return () => controller.abort();
  }, [effectiveQuery, searchRetry, selectedColor]);

  // Filter already-selected parts out of the displayed results
  const displayedResults = useMemo(
    () => searchResults.filter(r => !selectedParts.some(s => s.partId === r.partId)),
    [searchResults, selectedParts]
  );

  // Find matching bags when selected parts change (debounced so rapid
  // quantity clicks only fire one API call)
  useEffect(() => {
    if (!selectionReady) return;
    if (selectedParts.length === 0) {
      return;
    }
    const controller = new AbortController();
    const findBags = async () => {
      const selectionRevision = selectionRevisionRef.current;
      setResultsLoading(true);
      setError(null);
      try {
        const results = await api.findBagsWithParts(
          selectedParts.map(p => ({ partId: p.partId, quantity: p.quantity || 1 })),
          controller.signal,
          'near'
        );
        if (controller.signal.aborted) return;
        setMatchingResults(results);
        if (trackedResultsRevisionRef.current !== selectionRevision) {
          trackedResultsRevisionRef.current = selectionRevision;
          const bagCount = results.reduce((sum, set) => sum + set.bags.length, 0);
          const hasExactMatch = results.some(set => set.bags.some(bag => bag.matchType === 'exact'));
          trackEvent('Find My Bag Results', {
            match_type: results.length === 0 ? 'zero' : hasExactMatch ? 'exact' : 'partial',
            selected_parts: countBucket(selectedParts.length),
            set_count: countBucket(results.length),
            bag_count: countBucket(bagCount),
          });
        }
      } catch (err) {
        if (err.name === 'AbortError' || controller.signal.aborted) return;
        setError(friendlyMessage(err));
        if (trackedResultsRevisionRef.current !== selectionRevision) {
          trackedResultsRevisionRef.current = selectionRevision;
          trackEvent('Find My Bag Results Failed', {
            selected_parts: countBucket(selectedParts.length),
          });
        }
      } finally {
        if (!controller.signal.aborted) setResultsLoading(false);
      }
    };
    const timeoutId = setTimeout(findBags, 400);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [selectedParts, resultsRetry, selectionReady]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = e => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const addPart = (part, source = 'search') => {
    const partId = normalizeElementId(part?.partId);
    if (!partId) {
      setSelectionFeedback('Enter a numeric element ID, for example 6254045.');
      return false;
    }
    if (selectedParts.some(selected => selected.partId === partId)) {
      setSelectionFeedback(`Part ${partId} is already selected.`);
      return false;
    }
    if (selectedParts.length >= MAX_FIND_MY_BAG_PARTS) {
      setSelectionFeedback(
        `You can select up to ${MAX_FIND_MY_BAG_PARTS} parts. Remove one before adding another.`
      );
      return false;
    }

    selectionRevisionRef.current += 1;
    setSelectedParts(prev => [
      ...prev,
      {
        ...part,
        partId,
        quantity: 1,
        manuallyEntered: source === 'manual',
      },
    ]);
    setSelectionFeedback(`${source === 'manual' ? 'Added element ID' : 'Selected part'} ${partId}.`);
    setShareFeedback('');
    trackEvent('Find My Bag Part Selected', {
      selected_parts: countBucket(selectedParts.length + 1),
      entry_method: source,
    });
    setSearchQuery('');
    setSearchResults([]);
    setDropdownOpen(false);
    setActiveOptionIndex(-1);
    inputRef.current?.focus();
    return true;
  };

  const addManualPart = async () => {
    const partId = normalizeElementId(searchQuery);
    if (!partId) {
      setSelectionFeedback('Enter a numeric element ID, for example 6254045.');
      return;
    }
    if (selectedParts.some(selected => selected.partId === partId)) {
      setSelectionFeedback(`Part ${partId} is already selected.`);
      return;
    }
    if (selectedParts.length >= MAX_FIND_MY_BAG_PARTS) {
      setSelectionFeedback(
        `You can select up to ${MAX_FIND_MY_BAG_PARTS} parts. Remove one before adding another.`
      );
      return;
    }

    setResolvingManualId(true);
    setSelectionFeedback(`Checking element ID ${partId}...`);
    try {
      const resolved = await api.resolvePartsGlobal([partId]);
      if (!resolved[0]) {
        setSelectionFeedback("We couldn't find that element ID.");
        return;
      }
      addPart(resolved[0], 'manual');
    } catch (err) {
      notifyError(err, { title: 'Element ID lookup failed', id: 'findmybag-resolve' });
      setSelectionFeedback(friendlyMessage(err));
    } finally {
      setResolvingManualId(false);
    }
  };

  const removePart = partId => {
    if (!selectedParts.some(p => p.partId === partId)) return;
    selectionRevisionRef.current += 1;
    const nextParts = selectedParts.filter(p => p.partId !== partId);
    setSelectedParts(nextParts);
    if (nextParts.length === 0) {
      setMatchingResults([]);
      setResultsLoading(false);
      setError(null);
    }
    setSelectionFeedback(`Removed part ${partId}.`);
    setShareFeedback('');
  };

  const updateQuantity = (partId, delta) => {
    const part = selectedParts.find(p => p.partId === partId);
    if (!part) return;
    const nextQuantity = Math.max(1, Math.min(99, (part.quantity || 1) + delta));
    if (nextQuantity === (part.quantity || 1)) return;
    selectionRevisionRef.current += 1;
    setSelectedParts(prev => prev.map(p => (p.partId === partId ? { ...p, quantity: nextQuantity } : p)));
    setSelectionFeedback(`Quantity for part ${partId} changed to ${nextQuantity}.`);
    setShareFeedback('');
  };

  const clearAllParts = () => {
    if (selectedParts.length === 0) return;
    selectionRevisionRef.current += 1;
    setSelectedParts([]);
    setMatchingResults([]);
    setSelectionFeedback('Cleared all selected parts.');
    setShareFeedback('');
    inputRef.current?.focus();
  };

  useEffect(() => () => clearTimeout(copyFeedbackTimerRef.current), []);

  const copySelectionLink = async () => {
    const url = new URL(window.location.href);
    const serialized = serializeFindMyBagSelection(selectedParts);
    if (serialized) url.searchParams.set(FIND_MY_BAG_QUERY_KEY, serialized);

    try {
      await copyToClipboard(url.toString());
      setShareFeedback('Link copied to clipboard.');
      notifySuccess('You can paste this search anywhere.', { title: 'Link copied' });
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = setTimeout(() => setShareFeedback(''), 3000);
      trackEvent('Find My Bag Search Shared', {
        selected_parts: countBucket(selectedParts.length),
      });
    } catch (err) {
      setShareFeedback('Could not copy the link. Please try again.');
      notifyError(err, { title: 'Could not copy link', report: false });
    }
  };

  const totalBags = matchingResults.reduce((sum, set) => sum + set.bags.length, 0);
  const partOptionsOpen = dropdownOpen && (displayedResults.length > 0 || !!selectedColor);
  const activeOptionId =
    partOptionsOpen && activeOptionIndex >= 0 ? `${PART_OPTIONS_ID}-option-${activeOptionIndex}` : undefined;

  return (
    <>
      <PageHero
        icon={IconZoomQuestion}
        iconColor="#099268"
        iconBg="#e6fcf5"
        title="Find My Bag"
        subtitle="Enter a few brick IDs to find which set and bag they belong to"
      />
      <Container size="md" py="xl">
        <Stack gap="xl">
          {/* Search & Selected Parts */}
          <Paper
            p="xl"
            radius="lg"
            style={{
              backgroundColor: '#fff',
              border: '1px solid #e9ecef',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Stack gap="md">
              {/* Search input with dropdown */}
              <div ref={searchRef} style={{ position: 'relative' }}>
                <label htmlFor="findmybag-part-search" className="sr-only">
                  Search LEGO parts
                </label>
                <TextInput
                    ref={inputRef}
                    id="findmybag-part-search"
                    placeholder="Search parts or enter an element ID"
                    size="lg"
                    value={searchQuery}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-haspopup="listbox"
                    aria-expanded={partOptionsOpen}
                    aria-controls={partOptionsOpen ? PART_OPTIONS_ID : undefined}
                    aria-activedescendant={activeOptionId}
                    aria-describedby={`${PART_SEARCH_STATUS_ID} ${SELECTION_STATUS_ID}`}
                    aria-busy={searchLoading || resolvingManualId}
                    onChange={e => {
                      const nextQuery = e.currentTarget.value;
                      setSearchQuery(nextQuery);
                      setActiveOptionIndex(-1);
                      setSelectionFeedback('');
                      if (nextQuery.length >= 2) {
                        setDropdownOpen(true);
                      } else if (!selectedColor) {
                        setSearchResults([]);
                        setDropdownOpen(false);
                        setSearchLoading(false);
                      }
                    }}
                    onFocus={() => {
                      if (displayedResults.length > 0 || selectedColor) setDropdownOpen(true);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        if (displayedResults.length === 0) return;
                        e.preventDefault();
                        setDropdownOpen(true);
                        setActiveOptionIndex(current => {
                          if (current < 0) return e.key === 'ArrowDown' ? 0 : displayedResults.length - 1;
                          const direction = e.key === 'ArrowDown' ? 1 : -1;
                          return (current + direction + displayedResults.length) % displayedResults.length;
                        });
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (
                          partOptionsOpen &&
                          activeOptionIndex >= 0 &&
                          displayedResults[activeOptionIndex]
                        ) {
                          addPart(displayedResults[activeOptionIndex]);
                        } else if (!resolvingManualId) {
                          void addManualPart();
                        }
                      } else if (e.key === 'Escape') {
                        if (partOptionsOpen) e.preventDefault();
                        setDropdownOpen(false);
                        setActiveOptionIndex(-1);
                      } else if (e.key === 'Tab') {
                        setDropdownOpen(false);
                        setActiveOptionIndex(-1);
                      }
                    }}
                    leftSection={
                      searchLoading || resolvingManualId ? (
                        <Loader size={18} />
                      ) : selectedColor ? (
                        <Box
                          aria-hidden="true"
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: `#${(COMMON_FILTER_COLORS.find(c => c.name === selectedColor) || {}).hex || 'ccc'}`,
                            border: '1.5px solid rgba(0,0,0,0.2)',
                          }}
                        />
                      ) : (
                        <IconSearch size={20} aria-hidden="true" style={{ color: '#66707a' }} />
                      )
                    }
                    rightSection={
                      searchQuery || selectedColor ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          radius="xl"
                          size="sm"
                          aria-label="Clear part search"
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedColor(null);
                            setSearchResults([]);
                            setDropdownOpen(false);
                            setActiveOptionIndex(-1);
                            inputRef.current?.focus();
                          }}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      ) : null
                    }
                    rightSectionPointerEvents="all"
                    radius="xl"
                    classNames={{ input: 'main-search-input' }}
                  />
                <span
                  id={PART_SEARCH_STATUS_ID}
                  className="sr-only"
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {resolvingManualId
                    ? 'Checking the element ID.'
                    : searchLoading
                      ? 'Searching LEGO parts.'
                    : effectiveQuery.length >= 2
                      ? displayedResults.length === 0
                        ? 'No matching LEGO parts found.'
                        : `${displayedResults.length} matching ${displayedResults.length === 1 ? 'part' : 'parts'} available.`
                      : 'Enter at least two characters or choose a color to search.'}
                </span>

                {/* Search Results Dropdown */}
                <Transition mounted={partOptionsOpen} transition="pop-top-left" duration={150}>
                  {transitionStyles => (
                    <Paper
                      shadow="lg"
                      radius="lg"
                      style={{
                        ...transitionStyles,
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 200,
                        marginTop: 8,
                        border: '1px solid #e9ecef',
                        overflow: 'hidden',
                        maxHeight: 360,
                        overflowY: 'auto',
                      }}
                    >
                      {/* Kleurenstrip: sticky bovenin de dropdown - verfijnen
                        zonder dat de chips de pagina-layout vervuilen */}
                      <Box
                        style={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          backgroundColor: '#fff',
                          borderBottom: '1px solid #f1f3f4',
                          padding: '10px 12px',
                          display: 'flex',
                          gap: 6,
                          overflowX: 'auto',
                          scrollbarWidth: 'none',
                        }}
                      >
                        {COMMON_FILTER_COLORS.map(c => {
                          const selected = selectedColor === c.name;
                          return (
                            <Box
                              key={c.name}
                              component="button"
                              type="button"
                              onClick={() => {
                                const nextColor = selected ? null : c.name;
                                setSelectedColor(nextColor);
                                setActiveOptionIndex(-1);
                                if (!nextColor && searchQuery.length < 2) {
                                  setSearchResults([]);
                                  setDropdownOpen(false);
                                  setSearchLoading(false);
                                }
                              }}
                              aria-pressed={selected}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                flexShrink: 0,
                                padding: '3px 10px',
                                borderRadius: 999,
                                cursor: 'pointer',
                                fontSize: 12.5,
                                fontWeight: selected ? 700 : 500,
                                fontFamily: 'inherit',
                                backgroundColor: selected ? '#e7f5ff' : '#f8f9fa',
                                color: selected ? '#1971c2' : '#495057',
                                border: selected ? '1px solid #74c0fc' : '1px solid #e9ecef',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: `#${c.hex}`,
                                  border: '1px solid rgba(0,0,0,0.15)',
                                  flexShrink: 0,
                                }}
                              />
                              {c.name}
                            </Box>
                          );
                        })}
                      </Box>
                      <Box id={PART_OPTIONS_ID} role="listbox" aria-label="Matching LEGO parts">
                        {displayedResults.length === 0 && (
                          <Text size="sm" c="dimmed" p="md">
                            No parts match your search.
                          </Text>
                        )}
                        {displayedResults.map((part, index) => (
                          <Group
                            key={part.partId}
                            id={`${PART_OPTIONS_ID}-option-${index}`}
                            component="button"
                            type="button"
                            role="option"
                            aria-selected={index === activeOptionIndex}
                            tabIndex={-1}
                            gap="sm"
                            style={{
                              width: '100%',
                              padding: '12px 16px',
                              cursor: 'pointer',
                              transition: 'background-color 0.1s ease',
                              border: 0,
                              borderBottom: '1px solid #f1f3f4',
                              backgroundColor: index === activeOptionIndex ? '#f1f3f5' : '#fff',
                              color: 'inherit',
                              font: 'inherit',
                              textAlign: 'left',
                            }}
                            onClick={() => addPart(part)}
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={() => setActiveOptionIndex(index)}
                            wrap="nowrap"
                          >
                            {/* Part image */}
                            <Box
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 8,
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                overflow: 'hidden',
                              }}
                            >
                              <Image
                                src={getThumbUrl(part.imageUrl, 96)}
                                alt={part.partId}
                                fit="contain"
                                w={36}
                                h={36}
                                loading="lazy"
                                decoding="async"
                                fallbackSrc="https://placehold.co/36x36?text=?"
                              />
                            </Box>

                            {/* Color dot */}
                            {part.colorHex && (
                              <Box
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: `#${String(part.colorHex).replace(/^#/, '')}`,
                                  border: '1px solid rgba(0,0,0,0.1)',
                                  flexShrink: 0,
                                }}
                              />
                            )}

                            {/* Part info */}
                            <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                              {part.partName && (
                                <Text size="sm" fw={600} style={{ color: '#212529' }} lineClamp={1}>
                                  {part.partName}
                                </Text>
                              )}
                              <Group gap="xs" wrap="nowrap">
                                <Text
                                  size="xs"
                                  c={part.partName ? 'dimmed' : 'dark'}
                                  fw={part.partName ? 400 : 600}
                                >
                                  {part.partId}
                                </Text>
                                {part.colorName && (
                                  <Text size="xs" c="dimmed" truncate>
                                    {part.colorName}
                                  </Text>
                                )}
                                {part.blPartId && (
                                  <Text size="xs" c="dimmed">
                                    BL: {part.blPartId}
                                  </Text>
                                )}
                              </Group>
                            </Stack>

                            <Badge
                              size="sm"
                              variant="light"
                              color="blue"
                              radius="sm"
                              style={{ flexShrink: 0 }}
                            >
                              Select
                            </Badge>
                          </Group>
                        ))}
                      </Box>
                    </Paper>
                  )}
                </Transition>
              </div>

              <Text size="xs" c="dimmed">
                Search by part name, color, BrickLink ID, or element ID. If you already know the element ID,
                enter it and press Enter.
              </Text>

              {/* Selected parts stay intentionally list-like on mobile: every
                quantity control has room for a clear label and touch target. */}
              {selectedParts.length > 0 && (
                <Stack gap="sm">
                  <Group justify="space-between" align="center" gap="sm" wrap="wrap">
                    <Text fw={700} size="sm" style={{ color: '#343a40' }}>
                      Selected parts ({selectedParts.length}/{MAX_FIND_MY_BAG_PARTS})
                    </Text>
                    <SimpleGrid cols={2} spacing="xs" style={{ width: '100%' }}>
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        color={shareFeedback === 'Link copied to clipboard.' ? 'teal' : 'blue'}
                        leftSection={shareFeedback === 'Link copied to clipboard.'
                          ? <IconCheck size={14} aria-hidden="true" />
                          : <IconCopy size={14} aria-hidden="true" />}
                        onClick={copySelectionLink}
                        fullWidth
                        style={{ minHeight: 44 }}
                      >
                        {shareFeedback === 'Link copied to clipboard.' ? 'Link copied' : 'Copy link'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} aria-hidden="true" />}
                        onClick={clearAllParts}
                        fullWidth
                        style={{ minHeight: 44 }}
                      >
                        Clear all
                      </Button>
                    </SimpleGrid>
                  </Group>

                  <SimpleGrid
                    cols={{ base: 1, sm: 2 }}
                    spacing="xs"
                    role="list"
                    aria-label="Selected LEGO parts"
                  >
                    {selectedParts.map(part => (
                      <Paper
                        key={part.partId}
                        role="listitem"
                        radius="md"
                        p="sm"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          border: '1px solid #dee2e6',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Box
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 8,
                            flexShrink: 0,
                            backgroundColor: '#f8f9fa',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {part.imageUrl ? (
                            <Image
                              src={getThumbUrl(part.imageUrl, 96)}
                              alt=""
                              fit="contain"
                              w={36}
                              h={36}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <Text size="xs" fw={700} c="dimmed">
                              ID
                            </Text>
                          )}
                        </Box>

                        <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={6} wrap="nowrap">
                            {part.colorHex && (
                              <Box
                                aria-hidden="true"
                                style={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                  backgroundColor: `#${String(part.colorHex).replace(/^#/, '')}`,
                                  border: '1px solid rgba(0,0,0,0.15)',
                                }}
                              />
                            )}
                            <Text size="sm" fw={600} lineClamp={1} style={{ color: '#212529' }}>
                              {part.partName || `Element ${part.partId}`}
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            Element ID {part.partId}{part.colorName ? ` · ${part.colorName}` : ''}
                          </Text>
                        </Stack>

                        <Group
                          gap={0}
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: 14,
                            border: '1px solid #dee2e6',
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          <ActionIcon
                            size={44}
                            variant="subtle"
                            color="blue"
                            onClick={() => updateQuantity(part.partId, -1)}
                            disabled={part.quantity <= 1}
                            aria-label={`Decrease quantity for ${part.partName || `part ${part.partId}`}`}
                            style={{ borderRadius: 0 }}
                          >
                            <IconMinus size={14} aria-hidden="true" />
                          </ActionIcon>
                          <Text
                            size="sm"
                            fw={700}
                            aria-label={`Quantity ${part.quantity || 1}`}
                            style={{
                              minWidth: 32,
                              textAlign: 'center',
                              lineHeight: '44px',
                              userSelect: 'none',
                            }}
                          >
                            {part.quantity || 1}
                          </Text>
                          <ActionIcon
                            size={44}
                            variant="subtle"
                            color="blue"
                            onClick={() => updateQuantity(part.partId, 1)}
                            disabled={part.quantity >= 99}
                            aria-label={`Increase quantity for ${part.partName || `part ${part.partId}`}`}
                            style={{ borderRadius: 0 }}
                          >
                            <IconPlus size={14} aria-hidden="true" />
                          </ActionIcon>
                        </Group>

                        <ActionIcon
                          size={44}
                          variant="subtle"
                          color="gray"
                          onClick={() => removePart(part.partId)}
                          aria-label={`Remove ${part.partName || `part ${part.partId}`} from search`}
                          style={{ flexShrink: 0 }}
                        >
                          <IconX size={14} aria-hidden="true" />
                        </ActionIcon>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Stack>
              )}

              <Text
                id={SELECTION_STATUS_ID}
                size="sm"
                c={
                  selectionFeedback.includes('up to') ||
                  selectionFeedback.includes('already') ||
                  selectionFeedback.includes('numeric')
                    ? 'orange'
                    : 'dimmed'
                }
                role="status"
                aria-live="polite"
                aria-atomic="true"
                mih={20}
              >
                {shareFeedback || selectionFeedback}
              </Text>

              {/* Hint text when no parts selected */}
              {selectionReady && selectedParts.length === 0 && !searchQuery && (
                <Text size="sm" c="dimmed" ta="center" mb={-8}>
                  Tip: turn a LEGO brick over to look for its element ID, then add a few distinctive parts for
                  a more precise match.
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Results Section */}
          {selectedParts.length > 0 && (
            <Stack gap="md">
              {/* Results header */}
              <Group justify="space-between" align="center">
                <Title
                  order={2}
                  fw={600}
                  size="lg"
                  aria-live="polite"
                  aria-atomic="true"
                  style={{ color: '#212529' }}
                >
                  {resultsLoading
                    ? 'Searching...'
                    : matchingResults.length === 0
                      ? 'No matches found'
                      : `${matchingResults.length} ${matchingResults.length === 1 ? 'set' : 'sets'}, ${totalBags} ${totalBags === 1 ? 'bag' : 'bags'}`}
                </Title>
                {!resultsLoading && matchingResults.length > 0 && (
                  <Badge size="lg" variant="light" color="blue" radius="md">
                    {selectedParts.length} {selectedParts.length === 1 ? 'brick' : 'bricks'} selected
                  </Badge>
                )}
              </Group>
              {!resultsLoading && !error && matchingResults.length > 0 && (
                <Text size="sm" c="dimmed">
                  Full matches appear first. Partial matches show exactly which parts or quantities are still
                  missing.
                </Text>
              )}

              {error && (
                <Alert
                  role="alert"
                  title="Could not find matching bags"
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  radius="md"
                >
                  <Group justify="space-between" align="center" gap="sm">
                    <Text size="sm">{error}</Text>
                    <Button
                      size="compact-sm"
                      variant="light"
                      color="red"
                      onClick={() => setResultsRetry(value => value + 1)}
                    >
                      Try again
                    </Button>
                  </Group>
                </Alert>
              )}

              {resultsLoading && (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} height={180} radius="lg" />
                  ))}
                </SimpleGrid>
              )}

              {!resultsLoading && !error && matchingResults.length === 0 && (
                <Paper
                  p="xl"
                  radius="lg"
                  ta="center"
                  style={{
                    backgroundColor: '#fff8f0',
                    border: '1px solid #ffe0cc',
                  }}
                >
                  <Text size="md" c="dimmed" mb="xs">
                    No bags contain any of the selected parts.
                  </Text>
                  <Text size="sm" c="dimmed">
                    Double-check the element IDs or try another distinctive part.
                  </Text>
                </Paper>
              )}

              {!resultsLoading && !error && matchingResults.length > 0 && (
                <Stack gap="md">
                  {matchingResults.map(set => (
                    <Card
                      key={set.setId}
                      component="article"
                      padding={0}
                      radius="lg"
                      className="set-card"
                      style={{ overflow: 'hidden' }}
                    >
                      <Link
                        href={`/set/${set.setId}?bag=${encodeURIComponent(set.bags[0]?.bagNumber ?? '')}&parts=${selectedParts.map(sp => sp.partId).join(',')}`}
                        onClick={() =>
                          trackEvent('Find My Bag Result Opened', {
                            selected_parts: countBucket(selectedParts.length),
                            result_count: countBucket(matchingResults.length),
                          })
                        }
                        aria-label={`Open ${set.setName}, set ${set.setId}, at the first matching bag`}
                        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                      >
                        <Group gap="md" p="md" wrap="nowrap" align="center">
                          <Box
                            style={{
                              width: 72,
                              height: 72,
                              borderRadius: 12,
                              backgroundColor: '#f8f9fa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              overflow: 'hidden',
                            }}
                          >
                            <Image
                              src={
                                set.setImageUrl
                                  ? getThumbUrl(set.setImageUrl, 192)
                                  : 'https://placehold.co/72x72?text=No+Image'
                              }
                              alt={set.setName}
                              fit="contain"
                              w={64}
                              h={64}
                              loading="lazy"
                              decoding="async"
                            />
                          </Box>

                          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                            <Group gap="xs" wrap="wrap">
                              <Text size="xs" fw={600} c="dimmed">
                                #{set.setId}
                              </Text>
                              {set.setTheme && (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  radius="sm"
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    color: '#495057',
                                  }}
                                >
                                  {set.setTheme}
                                </Badge>
                              )}
                              <VerificationBadge
                                verified={set.setVerified}
                                showUnverified={false}
                                iconOnly={true}
                                size="sm"
                              />
                              {collectionIds.has(String(set.setId)) && (
                                <Badge
                                  size="xs"
                                  variant="light"
                                  color="green"
                                  radius="sm"
                                  leftSection={<IconCheck size={10} />}
                                  style={{ textTransform: 'none' }}
                                >
                                  In your collection
                                </Badge>
                              )}
                            </Group>
                            <Text
                              component="h3"
                              m={0}
                              fw={600}
                              size="sm"
                              lineClamp={2}
                              style={{ color: '#212529', lineHeight: 1.4 }}
                            >
                              {set.setName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Open the first suggested bag
                            </Text>
                          </Stack>
                          <IconExternalLink
                            size={18}
                            aria-hidden="true"
                            color="#66707a"
                            style={{ flexShrink: 0 }}
                          />
                        </Group>
                      </Link>

                      <Stack gap="sm" px="md" pb="md">
                        {set.bags.map(bag => {
                          const displayLabel =
                            bag.displayNumbers && bag.displayNumbers.length > 1
                              ? bag.displayNumbers.join('+')
                              : bag.bagNumber === 'Extra parts'
                                ? 'Extra'
                                : bag.bagNumber;
                          const partMatches = findBagPartMatches(
                            selectedParts,
                            bag.matchedParts,
                            bag.partMatches
                          );
                          const summary = summarizeBagMatch(partMatches);
                          const isExactMatch = bag.matchType === 'exact';
                          const summaryLabel = isExactMatch ? summary.label : 'Partial match';
                          const summaryColor = isExactMatch ? summary.color : 'gray';
                          const matchDescription = isExactMatch
                            ? summary.description
                            : `${bag.presentPartCount ?? 0} of ${bag.requestedPartCount ?? selectedParts.length} selected part types are present; ${bag.matchedPartCount ?? 0} contain enough. ${bag.matchedQuantity ?? 0} of ${bag.requestedQuantity ?? selectedParts.reduce((sum, part) => sum + part.quantity, 0)} requested pieces are accounted for.`;

                          return (
                            <Paper
                              key={bag.bagId}
                              radius="md"
                              p="md"
                              style={{
                                border: isExactMatch ? '1px solid #b2f2bb' : '1px solid #ffe8a1',
                                backgroundColor: isExactMatch ? '#f8fff9' : '#fffdf5',
                              }}
                            >
                              <Stack gap="sm">
                                <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
                                  <Group gap="xs">
                                    <IconMoneybag
                                      size={18}
                                      aria-hidden="true"
                                      color={isExactMatch ? '#2b8a3e' : '#5c5f66'}
                                    />
                                    <Text fw={700} size="sm" style={{ color: '#212529' }}>
                                      Bag {displayLabel}
                                    </Text>
                                  </Group>
                                  <Badge
                                    size="sm"
                                    variant="light"
                                    color={summaryColor}
                                    style={{ textTransform: 'none' }}
                                  >
                                    {summaryLabel}
                                  </Badge>
                                </Group>

                                <Text size="sm" c="dimmed">
                                  {matchDescription}
                                </Text>

                                <Stack gap={6} role="list" aria-label={`Why bag ${displayLabel} matches`}>
                                  {partMatches.map(part => {
                                    const isSatisfied = part.status === 'satisfied';
                                    const isMissing = part.status === 'missing';
                                    const statusText = isSatisfied
                                      ? 'Contains enough'
                                      : isMissing
                                        ? 'Missing'
                                        : `Short by ${Math.abs(part.difference)}`;
                                    return (
                                      <Group
                                        key={part.partId}
                                        role="listitem"
                                        justify="space-between"
                                        gap="sm"
                                        wrap="nowrap"
                                        p={8}
                                        style={{
                                          backgroundColor: '#fff',
                                          border: '1px solid #e9ecef',
                                          borderRadius: 8,
                                        }}
                                      >
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                          <Text size="sm" fw={600} lineClamp={1} style={{ color: '#343a40' }}>
                                            {part.partName || `Element ${part.partId}`}
                                          </Text>
                                          <Text size="xs" c="dimmed">
                                            ID {part.partId} · Requested {part.requested} · Available{' '}
                                            {part.available ?? 'unknown'}
                                          </Text>
                                        </Box>
                                        <Badge
                                          size="sm"
                                          variant="light"
                                          color={isSatisfied ? 'green' : 'red'}
                                          style={{ flexShrink: 0, textTransform: 'none' }}
                                        >
                                          {statusText}
                                        </Badge>
                                      </Group>
                                    );
                                  })}
                                </Stack>

                                <Button
                                  component={Link}
                                  href={`/set/${set.setId}?bag=${encodeURIComponent(bag.bagNumber)}&parts=${selectedParts.map(sp => sp.partId).join(',')}`}
                                  variant="filled"
                                  color="blue"
                                  radius="xl"
                                  fullWidth
                                  leftSection={<IconMoneybag size={16} aria-hidden="true" />}
                                  rightSection={<IconExternalLink size={15} aria-hidden="true" />}
                                  aria-label={`Open bag ${displayLabel} of set ${set.setId}`}
                                  onClick={() =>
                                    trackEvent('Find My Bag Bag Opened', {
                                      selected_parts: countBucket(selectedParts.length),
                                      bag_count: countBucket(totalBags),
                                      match_type: bag.matchType || 'unknown',
                                    })
                                  }
                                  style={{ minHeight: 44 }}
                                >
                                  Open bag {displayLabel}
                                </Button>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          {/* How it works section */}
          {selectedParts.length === 0 && (
            <Paper
              p="lg"
              radius="lg"
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
              }}
            >
              <Title order={2} size="h4" mb="sm" style={{ color: '#495057' }}>
                How it works
              </Title>
              <Stack gap="xs">
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">
                    1.
                  </Text>{' '}
                  Search for a brick by name (e.g. &ldquo;2x4 plate&rdquo;), element ID, or BrickLink ID
                </Text>
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">
                    2.
                  </Text>{' '}
                  Select matching bricks from the search results
                </Text>
                <Text size="sm" c="dimmed">
                  <Text component="span" fw={600} c="dark">
                    3.
                  </Text>{' '}
                  The more bricks you add, the more precise the result
                </Text>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </>
  );
}
